<template>
  <button
    v-if="isVisible"
    :class="buttonClasses"
    :disabled="isDisabled"
    :title="tooltipText"
    v-bind="$attrs"
  >
    <slot />
  </button>
  <button
    v-else-if="showDisabled"
    :class="disabledButtonClasses"
    disabled
    :title="deniedTooltip || t('common.noPermissionAction')"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePermissions } from '@/composables/usePermissions'
import { type Permission } from '@/utils/permissions'

interface Props {
  /**
   * Single permission to check
   */
  permission?: Permission | string
  /**
   * Multiple permissions - check if user has ANY (OR logic)
   */
  permissions?: (Permission | string)[]
  /**
   * Check if user has ALL permissions (AND logic)
   */
  requireAll?: boolean
  /**
   * Optional role to check
   */
  role?: string
  /**
   * Show disabled button when user lacks permission
   */
  showDisabled?: boolean
  /**
   * CSS classes for the button
   */
  class?: string
  /**
   * Whether the button is disabled (independent of permissions)
   */
  disabled?: boolean
  /**
   * Tooltip text when permission denied
   */
  deniedTooltip?: string
}

const props = withDefaults(defineProps<Props>(), {
  requireAll: false,
  showDisabled: false,
  disabled: false,
  deniedTooltip: '',
})

const { t } = useI18n()
const { can, canAny, canAll, hasUserRole } = usePermissions()

// Determine if element should be visible
const isVisible = computed(() => {
  // Check permission
  let hasPermissionAccess = true
  if (props.permission) {
    hasPermissionAccess = can(props.permission)
  } else if (props.permissions && props.permissions.length > 0) {
    hasPermissionAccess = props.requireAll
      ? canAll(props.permissions)
      : canAny(props.permissions)
  }

  // Check role
  let hasRoleAccess = true
  if (props.role) {
    hasRoleAccess = hasUserRole(props.role)
  }

  return hasPermissionAccess && hasRoleAccess
})

const isDisabled = computed(() => props.disabled)

const buttonClasses = computed(() => {
  const baseClasses = props.class || ''
  return `${baseClasses} ${isDisabled.value ? 'opacity-50 cursor-not-allowed' : ''}`
})

const disabledButtonClasses = computed(() => {
  const baseClasses = props.class || ''
  return `${baseClasses} opacity-40 cursor-not-allowed`
})

const tooltipText = computed(() => {
  return props.disabled ? t('common.actionUnavailable') : ''
})
</script>
